/**
 * What a cell looks like when nobody is editing it.
 *
 * A cell is a `<span>` and there are thousands of them, so nothing here may be
 * expensive -- which is the other half of the grid's design, the first half
 * being that there is exactly one editor on screen. Every type renders as its
 * human-readable text until it has a presentation of its own; this map is where
 * a type says it has one.
 *
 * Exhaustive over `FieldTypeName` with `null` the ordinary answer, so a
 * twenty-eighth type is a compile error rather than a blank column. A type is
 * answered here when its value is a picture rather than a word, and each one
 * arrives with the configuration that decides how it looks: a control whose
 * effect cannot be seen is worse than one that is not there.
 *
 * **A reader is given a value, not a row.** That is what lets a formula reuse
 * them: a lookup of a rating column holds ratings, and the only thing that
 * differs from a rating column is where the value came from.
 */
import { Fragment, type ReactNode } from 'react'
import { Check, Paperclip } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, FieldOf, FieldTypeName, RowData } from '@/api/types'
import { cn } from '@/shared/lib/utils'
import { userSortName, type FieldContext } from '../../behavior/behavior'
import { fileLabel, fileThumbnail, normaliseFiles } from '../../files'
import {
  FORMULA_TYPE_BEHAVES_AS,
  isArrayFormulaType,
  unwrapArrayItem,
  type ArrayFormulaType,
} from '../../formula-types'
import { emailHref, phoneHref, urlHref } from '../../links'
import { cellValue, valueText } from '../../read'
import { selectColorStyle } from '../../select-options'
import { parseRating, ratingColor, ratingMaxOf } from '../../rating'
import { ratingIcon } from '../rating'

export interface CellDisplayProps {
  field: Field
  row: RowData
  /** Required but nullable, so the one call site can pass what it has. */
  context: FieldContext | undefined
  /** Whether the value has more than one line to run onto. A cell has not. */
  wrap?: boolean | undefined
}

interface CellReaderProps {
  value: unknown
  /** The field the value came out of, which is where its formatting lives. */
  field: Field
  context: FieldContext | undefined
  wrap?: boolean | undefined
}

type CellReader = (props: CellReaderProps) => ReactNode

/** One option, in the colour it was given. */
function Chip({ option }: { option: { value?: string; color?: string } }) {
  return (
    <span
      className="max-w-full truncate rounded px-1.5 py-0.5 text-xs"
      style={selectColorStyle(option.color)}
    >
      {option.value ?? ''}
    </span>
  )
}

const SingleSelectCell: CellReader = ({ value }) => {
  const option = value as { value?: string; color?: string } | null
  // An empty cell is empty rather than a chip with nothing in it: a coloured
  // box saying nothing reads as a value somebody cannot make out.
  return option === null || option === undefined ? null : <Chip option={option} />
}

const MultipleSelectCell: CellReader = ({ value }) => {
  if (!Array.isArray(value)) return null

  return (
    <span className="flex min-w-0 gap-1">
      {(value as { id?: number; value?: string; color?: string }[]).map(
        (option, index) => (
          <Chip key={option.id ?? index} option={option} />
        ),
      )}
    </span>
  )
}

/**
 * A rating, as the symbols it stands for.
 *
 * Only the symbols it has, not the ones it does not: a three out of ten is
 * three stars rather than three filled and seven empty, which is what fits in a
 * column and what upstream draws. The count that goes missing with the empty
 * ones is put back as text nobody sees, because "three stars" and "three out of
 * ten" are different facts and a column of symbols says neither out loud.
 */
const RatingCell: CellReader = ({ value, field }) => {
  const { t } = useTranslation()
  const max = ratingMaxOf(field)
  const rating = parseRating(value, max)
  const Icon = ratingIcon(field)
  const color = ratingColor(field)

  return (
    <span className="flex min-w-0 items-center gap-0.5">
      <span className="sr-only">{t('grid.ratingValue', { value: rating, max })}</span>
      {Array.from({ length: rating }, (_, index) => (
        <Icon
          key={index}
          aria-hidden
          className="size-4 shrink-0"
          style={{ color, fill: 'currentColor' }}
        />
      ))}
    </span>
  )
}

/**
 * A checkbox, ticked or not.
 *
 * The box is drawn either way, where an empty select cell is left blank: a
 * checkbox has no empty: it is off or it is on, and a blank cell in a column of
 * boxes reads as a row nobody has got to yet.
 */
const BooleanCell: CellReader = ({ value }) => {
  const { t } = useTranslation()
  const checked = value === true

  return (
    <span className="flex items-center">
      <span className="sr-only">{t(checked ? 'grid.checked' : 'grid.unchecked')}</span>
      <span
        aria-hidden
        className={cn(
          'flex size-4 items-center justify-center rounded-sm border',
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
        )}
      >
        {checked && <Check className="size-3" strokeWidth={3} />}
      </span>
    </span>
  )
}

/**
 * The files on a row, as pictures where there is a picture to show.
 *
 * An image is its own label, so it is drawn and nothing is written beside it.
 * Anything else is not: a column of identical grey icons says only how many
 * files there are, so a document is drawn as its name. Upstream draws a
 * mime-type icon instead, from a table of about twenty -- but the name already
 * ends in `.pdf`, and it says which of three contracts this is as well.
 *
 * The height is upstream's twenty-two pixels, which is what fits a row, and the
 * width is left to follow the picture: the thumbnail this uses is made 21 tall
 * and whatever wide, so a square box would crop every one of them.
 */
const FileCell: CellReader = ({ value }) => {
  const files = normaliseFiles(value)

  return (
    <span className="flex min-w-0 items-center gap-1">
      {files.map((file) => {
        const label = fileLabel(file)
        const picture = fileThumbnail(file, 'tiny')
        return picture === null ? (
          <span
            key={file.name}
            className="flex min-w-0 items-center gap-1 rounded border px-1 py-0.5 text-xs"
          >
            <Paperclip aria-hidden className="size-3 shrink-0 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </span>
        ) : (
          <img
            key={file.name}
            src={picture}
            alt={label}
            className="h-[22px] w-auto shrink-0 rounded border"
          />
        )
      })}
    </span>
  )
}

/**
 * One named thing: a row in another table, or a person.
 *
 * The same pill the link row editor offers its choices as, so a cell and the
 * editor opened over it are recognisably the same list.
 */
function Named({ children }: { children: ReactNode }) {
  return (
    <span className="max-w-full shrink-0 truncate rounded-full border border-input px-2 py-0.5 text-xs">
      {children}
    </span>
  )
}

/**
 * How many linked rows are drawn before the rest become a count.
 *
 * A link field holds as many rows as somebody has linked, which in a synced
 * table is hundreds -- enough to fill a cell, the dialog and the record of what
 * changed with pills nobody reads. What is past the cap is a number.
 */
const LINKS_SHOWN = 10

/**
 * The rows this one is linked to, each named by the other table's primary field.
 *
 * A pill each rather than one run of comma-separated text, for the reason the
 * select chips are: a comma between two names is indistinguishable from a comma
 * *in* one, and rows are named by people who use commas.
 *
 * Two things a link cell can hold besides a saved row. A row whose primary
 * field is empty has no name to show, and is named by its id -- the same
 * wording the editor uses, because they are the same row. And a bare string is
 * a name with no row behind it yet: text pasted into a link column is sent as
 * names, and what is shown until the server answers is what was pasted.
 */
const LinkRowCell: CellReader = ({ value, wrap }) => {
  const { t } = useTranslation()
  if (!Array.isArray(value)) return null

  const links = value as (string | { id?: number; value?: string })[]
  const shown = links.slice(0, LINKS_SHOWN)
  const rest = links.length - shown.length

  return (
    <span className={cn('flex min-w-0 items-center gap-1', wrap === true && 'flex-wrap')}>
      {shown.map((link, index) =>
        typeof link === 'string' ? (
          <Named key={`pasted:${String(index)}`}>{link}</Named>
        ) : (
          <Named key={link.id ?? `unsaved:${String(index)}`}>
            {link.value === undefined || link.value === ''
              ? t('link.unnamed', { id: link.id })
              : link.value}
          </Named>
        ),
      )}
      {rest > 0 && (
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {t('link.more', { count: rest })}
        </span>
      )}
    </span>
  )
}

/**
 * The people a cell names, by whatever the workspace calls them now.
 *
 * Not by the name the row remembers. A row records who did something at the
 * moment they did it, and somebody who has since changed their name would
 * otherwise appear under both -- so the workspace's answer wins, and the stored
 * name is what is left for somebody who has since left it.
 */
const PeopleCell: CellReader = ({ value, context }) => {
  const people = Array.isArray(value) ? value : value === null ? [] : [value]

  return (
    <span className="flex min-w-0 items-center gap-1">
      {(people as { id?: number }[]).map((person, index) => (
        <Named key={person.id ?? index}>{userSortName(person, context)}</Named>
      ))}
    </span>
  )
}

/**
 * A link, out of the tab order.
 *
 * Like the button that opens a row: the grid is one tab stop by design, and an
 * anchor per url cell would put eighty of them inside it. The row a link is on
 * can be opened, which is where the keyboard reaches one.
 *
 * A click on an unselected cell selects it and does *not* follow the link --
 * that rule is the grid's, in `RowBody`, because only the grid knows what was
 * selected before the click. Without it a url column could not be selected at
 * all without leaving the page.
 */
function Anchor({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      tabIndex={-1}
      className="truncate text-primary underline underline-offset-2"
    >
      {children}
    </a>
  )
}

/** A cell that is somewhere to go, written as the address it points at. */
function somewhereToGo(type: FieldTypeName, href: (value: string) => string): CellReader {
  return ({ value, field, context }) => {
    const text = valueText(type, field, value, context)
    if (text === '') return null
    return <Anchor href={href(text)}>{text}</Anchor>
  }
}

/**
 * The link that lets somebody outside edit this row.
 *
 * The value is the whole signed url -- a slug and a token of about two hundred
 * characters -- which is unreadable as link text and would fill the column. So
 * the link says what it does and the address stays in the href, where a copy of
 * the cell still finds it: `cellText` is what the clipboard writes, and that is
 * the url.
 *
 * Nothing at all when the field points at no form, or at one that has been
 * deleted: the backend answers null for every row, and eighty disabled buttons
 * saying so is worse than an empty column. The field's own configuration is
 * where that is said, and it says it there.
 */
const EditRowCell: CellReader = ({ value }) => {
  const { t } = useTranslation()
  // A string or nothing: the backend answers the whole url or null, never the
  // empty string, so there is no third case to defend against here.
  if (typeof value !== 'string') return null
  return <Anchor href={urlHref(value)}>{t('grid.editRow')}</Anchor>
}

/**
 * A formula that computed somewhere to go: `link()` and `button()`.
 *
 * They produce a label and a url together, which the text rendering has to
 * flatten into "label (url)" because it has only one string to say it in. Here
 * they can be what they are -- the label, pointing at the url.
 *
 * A pair with no url at all is written as its label and nothing more. That is
 * the only validity test made: the scheme allow list in `urlHref` is what
 * stops a computed address being an executable one, and beyond that a formula
 * that returns nonsense gets a link to nonsense, which is the answer it asked
 * for.
 */
const LinkLikeCell: CellReader = ({ value }) => {
  if (value === null || value === undefined) return null
  const pair =
    typeof value === 'string'
      ? { label: '', url: value }
      : (value as { label?: unknown; url?: unknown })
  const url = typeof pair.url === 'string' ? pair.url.trim() : ''
  const label = typeof pair.label === 'string' ? pair.label : ''
  const shown = label === '' ? url : label

  if (shown === '') return null
  if (url === '') return <span className="truncate">{shown}</span>
  return <Anchor href={urlHref(url)}>{shown}</Anchor>
}

/**
 * A formula whose result is one file rather than a column of them.
 *
 * The only entry type the backend does not wrap, and the only one whose value
 * is a single object where the field type it behaves as holds a list.
 */
const SingleFileCell: CellReader = (props) => FileCell({ ...props, value: [props.value] })

type FormulaField =
  FieldOf<'formula'> | FieldOf<'count'> | FieldOf<'rollup'> | FieldOf<'lookup'>

/** One entry of an array formula whose type has no presentation of its own. */
function Entry({ children }: { children: ReactNode }) {
  return (
    <span className="max-w-full shrink-0 truncate rounded border border-input px-1.5 py-0.5 text-xs">
      {children}
    </span>
  )
}

/**
 * A lookup, and every other formula whose result is a list.
 *
 * Each entry is drawn as the type `array_formula_type` says it is, so a lookup
 * of a checkbox column is a row of checkboxes rather than "true, false, true".
 * The field passed to each entry is the formula field, not the one looked up:
 * a lookup of a date column carries the date format it is to be shown in, and
 * the column it came from is in another table this grid has not loaded.
 *
 * An entry with no presentation is boxed rather than joined with commas, for
 * the reason a link row's names are: a comma between two entries cannot be told
 * from a comma inside one. An entry that renders as nothing at all is dropped,
 * which is upstream's behaviour and the only one that keeps a lookup over
 * mostly-empty rows readable -- at the cost of not saying how many rows were
 * looked at. A checkbox is not "nothing": `false` is a value, and its reader is
 * what decides that, not this.
 */
const ArrayFormulaCell: CellReader = ({ value, field, context }) => {
  const entryType = (field as FormulaField).array_formula_type
  if (!isArrayFormulaType(entryType) || !Array.isArray(value)) return null

  const reader = formulaReader(entryType)
  const asType = FORMULA_TYPE_BEHAVES_AS[entryType]

  return (
    <span className="flex min-w-0 items-center gap-1">
      {value.map((item: unknown, index) => {
        // Wrapped entries carry the id of the row they were looked up from,
        // which is a better key than a position that shifts whenever a link is
        // added above it.
        const key = (item as { id?: number } | null)?.id ?? index
        const entry = unwrapArrayItem(entryType, item)

        if (reader !== null) {
          return <Fragment key={key}>{reader({ value: entry, field, context })}</Fragment>
        }

        const text = valueText(asType, field, entry, context)
        return text === '' ? null : <Entry key={key}>{text}</Entry>
      })}
    </span>
  )
}

/**
 * A formula, and the three fields that are a formula with a form around them.
 *
 * A formula has no presentation of its own any more than it has a type of its
 * own: it is drawn as whichever ordinary type the backend says the result
 * behaves like. So a rollup that sums to a number is text, and a formula that
 * returns a select option is that option's chip.
 */
const FormulaCell: CellReader = (props) => {
  const formulaType = (props.field as FormulaField).formula_type

  if (formulaType === 'array') return <ArrayFormulaCell {...props} />
  if (!isArrayFormulaType(formulaType)) return <PlainText {...props} />

  const reader = formulaReader(formulaType)
  return reader === null ? <PlainText {...props} /> : reader(props)
}

const CELL_READERS = {
  text: null,
  long_text: null,
  url: somewhereToGo('url', urlHref),
  email: somewhereToGo('email', emailHref),
  number: null,
  rating: RatingCell,
  boolean: BooleanCell,
  date: null,
  last_modified: null,
  created_on: null,
  last_modified_by: PeopleCell,
  created_by: PeopleCell,
  duration: null,
  link_row: LinkRowCell,
  file: FileCell,
  single_select: SingleSelectCell,
  multiple_select: MultipleSelectCell,
  phone_number: somewhereToGo('phone_number', phoneHref),
  formula: FormulaCell,
  count: FormulaCell,
  rollup: FormulaCell,
  lookup: FormulaCell,
  multiple_collaborators: PeopleCell,
  uuid: null,
  autonumber: null,
  password: null,
  form_view_edit_row: EditRowCell,
} as const satisfies Record<FieldTypeName, CellReader | null>

/**
 * The three formula result types that are not drawn as the field type they
 * behave like.
 *
 * `link` and `button` behave as text because that is all a filter can do with
 * them, which is a different question from what they look like. A
 * `single_file` behaves as a file column, which holds a list where this holds
 * one.
 */
const FORMULA_CELL_READERS: Partial<Record<ArrayFormulaType, CellReader>> = {
  link: LinkLikeCell,
  button: LinkLikeCell,
  single_file: SingleFileCell,
}

function formulaReader(type: ArrayFormulaType): CellReader | null {
  return FORMULA_CELL_READERS[type] ?? CELL_READERS[FORMULA_TYPE_BEHAVES_AS[type]]
}

/**
 * A cell as the words it is made of.
 *
 * Not a fallback for a type nobody got round to: it is what most types are, and
 * what every `contains` filter matches against, so a type with no presentation
 * of its own is rendered by the same function the filters read.
 */
const PlainText: CellReader = ({ value, field, context }) => (
  <span className="truncate">{valueText(field.type, field, value, context)}</span>
)

/**
 * One value, drawn as the field it came out of would draw it.
 *
 * The row is not always there to read it from: a group header is given the
 * value the rows in the group share, which is a value with no cell. Exported
 * beside `CellDisplay` rather than under it, because the map has always been
 * keyed on the value -- see the note at the top of this file.
 */
export function ValueDisplay({ field, value, context, wrap }: CellReaderProps) {
  const reader: CellReader = CELL_READERS[field.type] ?? PlainText
  return reader({ value, field, context, wrap })
}

/** One cell, drawn. */
export function CellDisplay({ field, row, context, wrap }: CellDisplayProps) {
  return ValueDisplay({ field, value: cellValue(field, row), context, wrap })
}
